import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Collapse, Divider, Paper, Typography,
} from '@mui/material';
import AppShell from '../components/Layout/AppShell';
import PesquisasProntas from '../components/Pesquisa/PesquisasProntas';
import {
  CardWrapper, ButtonUI, ButtonOutlined, Field, PeriodoField, SectionTitle,
} from '../components/UI';
import {
  CAMPOS_SIMPLES, CAMPOS_COMPLEMENTARES, CAMPOS_AVANCADOS, MINHAS_INFORMACOES,
} from '../mocks/pesquisas.mock';
import * as C from '../theme/colors.constants';
import { rota } from '../prototipo.config';

/**
 * Pesquisa de Processos.
 *
 * Comportamento do "PESQUISA AVANÇADA": no projeto real ele abre um drawer
 * lateral de 500px. Nos prints novos os campos ficam na própria tela, então
 * aqui o botão faz um reveal inline (Collapse) de:
 *   - 4 campos extras (CPF/CNPJ Cliente, Estado do Devedor, Data do
 *     Agendamento, Período de envio para fase atual)
 *   - o bloco de Pesquisas prontas
 * O rótulo do botão alterna para "PESQUISA SIMPLES" quando expandido, mesma
 * lógica de `switchTypeSearch()` do controller real.
 */
export default function PesquisaProcessosPage() {
  const history = useHistory();
  const [avancada, setAvancada] = useState(false);
  const [valores, setValores] = useState({});

  const set = id => (valorOuEvento) => {
    const valor = valorOuEvento?.target ? valorOuEvento.target.value : valorOuEvento;
    setValores(v => ({ ...v, [id]: valor }));
  };

  const limpar = () => setValores({});

  const renderCampo = (campo) => {
    if (campo.tipo === 'periodo') {
      return (
        <PeriodoField
          key={campo.id}
          label={campo.label}
          valor={valores[campo.id] || {}}
          onChange={set(campo.id)}
        />
      );
    }
    return (
      <Field
        key={campo.id}
        label={campo.label}
        tipo={campo.tipo}
        options={campo.options}
        value={valores[campo.id] || ''}
        onChange={set(campo.id)}
      />
    );
  };

  // Layout do print: col.1 (campos), col.2 (campos + UF ao lado), col.3 (botões).
  const col1 = CAMPOS_SIMPLES.filter(c => c.col === 1);
  const col2 = CAMPOS_SIMPLES.filter(c => c.col === 2 && c.id !== 'uf');
  const campoUf = CAMPOS_SIMPLES.find(c => c.id === 'uf');

  return (
    <AppShell titulo="Pesquisa de processos">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 1.05vw, 26px)' }}>
        {/* ---------- Bloco 1: pesquisa simples + ações ---------- */}
        <CardWrapper>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 1.15fr) minmax(0, 0.5fr)' },
              columnGap: 'clamp(16px, 1.6vw, 44px)',
              rowGap: 'clamp(12px, 0.9vw, 22px)',
              alignItems: 'start',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 0.9vw, 22px)' }}>
              {col1.map(renderCampo)}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 0.9vw, 22px)' }}>
              {col2.map(campo => (campo.id === 'cidadeDevedor' ? (
                <Box key={campo.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                  <Box sx={{ flex: 1 }}>{renderCampo(campo)}</Box>
                  <Box sx={{ width: 'clamp(80px, 5vw, 120px)', flexShrink: 0 }}>
                    {renderCampo(campoUf)}
                  </Box>
                </Box>
              ) : (
                renderCampo(campo)
              )))}
            </Box>

            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 0.8vw, 20px)', pt: 2.5,
            }}
            >
              <ButtonUI onClick={() => history.push(rota('/processos'))} sx={{ minHeight: 40 }}>
                LOCALIZAR
              </ButtonUI>
              <ButtonOutlined onClick={() => setAvancada(v => !v)} sx={{ minHeight: 40 }}>
                {avancada ? 'PESQUISA SIMPLES' : 'PESQUISA AVANÇADA'}
              </ButtonOutlined>
              <ButtonOutlined onClick={limpar} sx={{ minHeight: 40 }}>
                LIMPAR
              </ButtonOutlined>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 0.9vw, 22px)' }}>
              {CAMPOS_COMPLEMENTARES.filter(c => c.col === 1).map(renderCampo)}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 0.9vw, 22px)' }}>
              {CAMPOS_COMPLEMENTARES.filter(c => c.col === 2).map(renderCampo)}
            </Box>
          </Box>
        </CardWrapper>

        {/* ---------- Bloco 3: só na pesquisa avançada ---------- */}
        <Collapse in={avancada} unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 1.05vw, 26px)' }}>
            <CardWrapper>
              <SectionTitle>Pesquisa avançada</SectionTitle>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
                  columnGap: 'clamp(16px, 1.6vw, 44px)',
                  rowGap: 'clamp(12px, 0.9vw, 22px)',
                  maxWidth: { lg: '68%' },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 0.9vw, 22px)' }}>
                  {CAMPOS_AVANCADOS.filter(c => c.col === 1).map(renderCampo)}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 0.9vw, 22px)' }}>
                  {CAMPOS_AVANCADOS.filter(c => c.col === 2).map(renderCampo)}
                </Box>
              </Box>
            </CardWrapper>

            <PesquisasProntas valores={valores} onChange={setValores} />
          </Box>
        </Collapse>

        {/* ---------- Bloco 4: Minhas informações ---------- */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              color: C.warmGrey, fontWeight: 700, mb: 1.25, px: 0.5,
            }}
          >
            Minhas informações
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' },
              gap: 'clamp(12px, 1.05vw, 26px)',
            }}
          >
            {MINHAS_INFORMACOES.map(c => (
              <Paper
                key={c.id}
                onClick={() => history.push(rota(`/processos?acao=${c.id}`))}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 'clamp(10px, 0.7vw, 18px)',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: '0 4px 12px rgba(13,43,82,0.16)' },
                }}
              >
                <Box
                  sx={{
                    minWidth: 'clamp(38px, 2.4vw, 52px)',
                    height: 'clamp(38px, 2.4vw, 52px)',
                    borderRadius: 2,
                    backgroundColor: C.badgeBlue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.navy,
                    fontWeight: 700,
                    fontSize: 'clamp(16px, 1vw, 24px)',
                  }}
                >
                  {c.total}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 'clamp(13px, 0.78vw, 18px)' }}>
                  {c.label}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      </Box>
    </AppShell>
  );
}
